/* MUSE Theme JS */

// ─── Cart ────────────────────────────────────────────────────────────────────

const MuseCart = {
  cart: null,

  async init() {
    this.drawer   = document.getElementById('cart-drawer');
    this.overlay  = document.getElementById('cart-overlay');
    this.itemsEl  = document.getElementById('cart-items');
    this.emptyEl  = document.getElementById('cart-empty');
    this.filledEl = document.getElementById('cart-filled');
    this.subtotalEl = document.getElementById('cart-subtotal');

    document.querySelectorAll('[data-cart-open]').forEach(btn =>
      btn.addEventListener('click', () => this.open())
    );
    document.querySelectorAll('[data-cart-close]').forEach(btn =>
      btn.addEventListener('click', () => this.close())
    );
    this.overlay?.addEventListener('click', () => this.close());

    await this.fetchAndRender();
  },

  async fetchAndRender() {
    const res = await fetch('/cart.js');
    this.cart = await res.json();
    this.render();
    this.updateBadge();
  },

  async addItem(variantId, quantity = 1) {
    const btn = document.querySelector(`[data-quick-add="${variantId}"]`) ||
                document.getElementById('add-to-cart');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });

    await this.fetchAndRender();
    this.open();

    if (btn) {
      btn.disabled = false;
      btn.textContent = btn.id === 'add-to-cart' ? 'Add to cart' : 'Add to cart';
    }
  },

  async updateLine(line, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    await this.fetchAndRender();
  },

  open() {
    this.drawer?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  },

  updateBadge() {
    const count = this.cart?.item_count ?? 0;
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  },

  money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  },

  render() {
    if (!this.cart) return;
    const empty = this.cart.item_count === 0;

    this.emptyEl?.classList.toggle('is-visible', empty);
    this.filledEl?.classList.toggle('is-visible', !empty);

    const titleCount = document.getElementById('cart-title-count');
    if (titleCount) titleCount.textContent = empty ? '' : `(${this.cart.item_count})`;

    if (this.subtotalEl) this.subtotalEl.textContent = this.money(this.cart.total_price);

    if (this.itemsEl) {
      this.itemsEl.innerHTML = this.cart.items.map((item, i) => `
        <div class="cart-line">
          <a href="${item.url}" class="cart-line__img" data-cart-close>
            ${item.image
              ? `<img src="${item.image}" alt="${this.esc(item.product_title)}" loading="lazy">`
              : ''}
          </a>
          <div class="cart-line__info">
            <a href="${item.url}" class="cart-line__title" data-cart-close>${this.esc(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title'
              ? `<p class="cart-line__variant">${this.esc(item.variant_title)}</p>` : ''}
            <div class="cart-line__footer">
              <div class="qty-stepper">
                <button class="qty-btn" data-line="${i + 1}" data-delta="-1" aria-label="Decrease">–</button>
                <span class="qty-val">${item.quantity}</span>
                <button class="qty-btn" data-line="${i + 1}" data-delta="1" aria-label="Increase">+</button>
              </div>
              <span class="cart-line__price">${this.money(item.line_price)}</span>
            </div>
          </div>
          <button class="cart-line__remove" data-line="${i + 1}" data-delta="-999" aria-label="Remove">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>
      `).join('');

      this.itemsEl.querySelectorAll('[data-line]').forEach(btn => {
        btn.addEventListener('click', () => {
          const line  = parseInt(btn.dataset.line);
          const delta = parseInt(btn.dataset.delta);
          const item  = this.cart.items[line - 1];
          if (!item) return;
          const newQty = delta === -999 ? 0 : Math.max(0, item.quantity + delta);
          this.updateLine(line, newQty);
        });
      });

      this.itemsEl.querySelectorAll('[data-cart-close]').forEach(el =>
        el.addEventListener('click', () => this.close())
      );
    }
  },

  esc(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  },
};

// ─── Header ──────────────────────────────────────────────────────────────────

function initHeader() {
  const header = document.getElementById('site-header');
  if (!header) return;

  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  // Mobile menu
  const menuBtn    = document.getElementById('menu-btn');
  const mobileMenu = document.getElementById('mobile-menu');
  const menuOverlay = document.getElementById('menu-overlay');

  menuBtn?.addEventListener('click', () => {
    const open = mobileMenu?.classList.toggle('is-open');
    menuOverlay?.classList.toggle('is-open', open);
    menuBtn.setAttribute('aria-expanded', String(!!open));
  });
  menuOverlay?.addEventListener('click', () => {
    mobileMenu?.classList.remove('is-open');
    menuOverlay?.classList.remove('is-open');
    menuBtn?.setAttribute('aria-expanded', 'false');
  });

  // Search
  const searchBtn   = document.getElementById('search-btn');
  const searchBar   = document.getElementById('search-bar');
  const searchInput = document.getElementById('search-input');

  searchBtn?.addEventListener('click', () => {
    const open = searchBar?.classList.toggle('is-open');
    if (open) setTimeout(() => searchInput?.focus(), 50);
  });
}

// ─── Product page ─────────────────────────────────────────────────────────────

function initProductPage() {
  if (!document.getElementById('product-form')) return;

  // Gallery
  const mainImg = document.getElementById('gallery-main');
  document.querySelectorAll('[data-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      if (mainImg) mainImg.src = thumb.dataset.src;
      document.querySelectorAll('[data-thumb]').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  // Variants
  const variantDataEl = document.getElementById('variant-data');
  if (!variantDataEl) return;
  const variants = JSON.parse(variantDataEl.textContent);

  const selected = {};
  const optionBtns = document.querySelectorAll('[data-option-btn]');

  // Seed from first variant
  if (variants[0]) {
    variants[0].options.forEach((val, i) => { selected[i] = val; });
  }

  function findVariant() {
    return variants.find(v => v.options.every((val, i) => selected[i] === val));
  }

  function updatePrice(variant) {
    const priceEl   = document.getElementById('product-price');
    const compareEl = document.getElementById('product-compare-price');
    const onSale    = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (priceEl) {
      priceEl.textContent = money(variant.price);
      priceEl.style.color = onSale ? 'var(--accent)' : 'var(--fg)';
    }
    if (compareEl) {
      compareEl.textContent = onSale ? money(variant.compare_at_price) : '';
      compareEl.style.display = onSale ? 'inline' : 'none';
    }
  }

  function updateAddBtn(variant) {
    const btn = document.getElementById('add-to-cart');
    if (!btn) return;
    if (!variant || !variant.available) {
      btn.textContent = 'Sold out';
      btn.disabled = true;
    } else {
      btn.textContent = 'Add to cart';
      btn.disabled = false;
    }
    // Stock indicator
    const dot  = document.getElementById('stock-dot');
    const text = document.getElementById('stock-text');
    if (dot && text) {
      const avail = variant?.available;
      dot.style.background = avail ? '#16a34a' : 'var(--faint)';
      text.textContent = avail ? 'In stock · ships in 1–2 business days' : 'Currently unavailable';
    }
  }

  function updateVariantInput(variant) {
    const input = document.getElementById('variant-id');
    if (input && variant) input.value = variant.id;
    if (variant) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  function updateGallery(variant) {
    if (!variant?.featured_image) return;
    const idx = variant.featured_image.position - 1;
    const thumb = document.querySelectorAll('[data-thumb]')[idx];
    if (thumb) thumb.click();
  }

  function applyVariant() {
    const variant = findVariant();
    if (!variant) return;
    updatePrice(variant);
    updateAddBtn(variant);
    updateVariantInput(variant);
    updateGallery(variant);
  }

  optionBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const pos = parseInt(btn.dataset.position);
      const val = btn.dataset.value;
      selected[pos] = val;
      document.querySelectorAll(`[data-option-btn][data-position="${pos}"]`).forEach(b =>
        b.classList.toggle('is-selected', b.dataset.value === val)
      );
      applyVariant();
    });
  });

  // Qty stepper
  const qtyInput = document.getElementById('qty-input');
  document.getElementById('qty-decrease')?.addEventListener('click', () => {
    const v = parseInt(qtyInput?.value || '1');
    if (qtyInput && v > 1) qtyInput.value = v - 1;
  });
  document.getElementById('qty-increase')?.addEventListener('click', () => {
    const v = parseInt(qtyInput?.value || '1');
    if (qtyInput) qtyInput.value = v + 1;
  });

  // Add to cart
  document.getElementById('add-to-cart')?.addEventListener('click', async () => {
    const variantId = document.getElementById('variant-id')?.value;
    const qty = parseInt(document.getElementById('qty-input')?.value || '1');
    if (variantId) await MuseCart.addItem(parseInt(variantId), qty);
  });

  // Accordions
  document.querySelectorAll('[data-accordion-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      const body = document.getElementById(btn.dataset.accordionBtn);
      const open = body?.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(!!open));
      const icon = btn.querySelector('.accordion-icon');
      if (icon) icon.style.transform = open ? 'rotate(45deg)' : 'none';
    });
  });

  applyVariant();
}

// ─── Quick-add (listing pages) ────────────────────────────────────────────────

function initQuickAdd() {
  document.querySelectorAll('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      const variantId = parseInt(btn.dataset.quickAdd);
      if (!variantId) return;
      const orig = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;
      await MuseCart.addItem(variantId, 1);
      btn.textContent = orig;
      btn.disabled = false;
    });
  });
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

function initFaq() {
  document.querySelectorAll('[data-faq-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      const panelId = btn.dataset.faqBtn;
      const panel   = document.getElementById(panelId);
      const wasOpen = btn.getAttribute('aria-expanded') === 'true';

      document.querySelectorAll('[data-faq-btn]').forEach(b => {
        b.setAttribute('aria-expanded', 'false');
        const p = document.getElementById(b.dataset.faqBtn);
        if (p) p.hidden = true;
        const icon = b.querySelector('.accordion-icon');
        if (icon) icon.style.transform = 'none';
      });

      if (!wasOpen && panel) {
        panel.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        const icon = btn.querySelector('.accordion-icon');
        if (icon) icon.style.transform = 'rotate(45deg)';
      }
    });
  });

  // Open first by default
  const first = document.querySelector('[data-faq-btn]');
  if (first) first.click();
}

// ─── Sort select ──────────────────────────────────────────────────────────────

function initSort() {
  const sel = document.getElementById('sort-select');
  if (!sel) return;
  sel.addEventListener('change', () => {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sel.value);
    window.location.href = url.toString();
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function money(cents) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

// ─── Boot ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', async () => {
  await MuseCart.init();
  initHeader();
  initProductPage();
  initQuickAdd();
  initFaq();
  initSort();
});
